import React, { useMemo } from 'react';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Link } from 'react-router-dom';
import Header from 'shared/components/Header';
import { tokens } from 'shared/theme';

const HUB_ACTIONS = [
  {
    key: 'settings',
    label: 'Settings',
    description:
      'Business units, processes, system owners, vendors, and regulations.',
    to: '/system-register/manage-details/settings',
    icon: SettingsOutlinedIcon,
  },
  {
    key: 'operation',
    label: 'Operation',
    description:
      'Data records, approvals, and deployments.',
    to: '/system-register/manage-details/operation',
    icon: BuildOutlinedIcon,
  },
];

const ManageSystemDetailsPage = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === 'dark';
  const primaryMain = theme.palette.primary.main;
  const secondaryMain = theme.palette.secondary.main;

  const cardSurface = isDark ? theme.palette.background.paper : '#ffffff';
  const titleColor = isDark ? colors.grey[100] : primaryMain;
  const bodyColor = isDark ? colors.grey[200] : colors.grey[600];

  const heroGradient = useMemo(
    () =>
      isDark
        ? `linear-gradient(135deg, ${alpha(primaryMain, 0.35)} 0%, ${colors.primary[400]} 45%, ${alpha(secondaryMain, 0.22)} 100%)`
        : `linear-gradient(135deg, ${alpha(primaryMain, 0.12)} 0%, #ffffff 55%, ${alpha(secondaryMain, 0.1)} 100%)`,
    [isDark, primaryMain, secondaryMain, colors.primary]
  );

  return (
    <Box m={{ xs: '12px', sm: '16px' }}>
      <Header title="Manage System Details" />

      <Box
        mt={1}
        mb={2}
        px={{ xs: 1.5, sm: 2 }}
        py={{ xs: 1.25, sm: 1.5 }}
        borderRadius={1.5}
        sx={{
          background: heroGradient,
          border: `1px solid ${alpha(primaryMain, isDark ? 0.45 : 0.18)}`,
          boxShadow: isDark ? 'none' : `0 2px 12px ${alpha(primaryMain, 0.06)}`,
        }}
      >
        <Box display="flex" alignItems="center" gap={1.25} flexWrap="wrap">
          <Box flex={1} minWidth={160}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ color: titleColor, lineHeight: 1.2 }}>
              Manage System Details
            </Typography>
            <Typography variant="caption" sx={{ color: bodyColor, display: 'block', lineHeight: 1.35 }}>
              Choose Settings for reference data, or Operation for supporting records.
            </Typography>
          </Box>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
        {HUB_ACTIONS.map((item) => {
          const Icon = item.icon;

          return (
            <Grid item xs={12} md={6} key={item.key} sx={{ minWidth: 0 }}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  backgroundColor: cardSurface,
                  border: `1px solid ${alpha(primaryMain, isDark ? 0.35 : 0.16)}`,
                  borderRadius: 2,
                  transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
                  boxShadow: isDark ? 'none' : `0 2px 12px ${alpha(primaryMain, 0.06)}`,
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    borderColor: secondaryMain,
                    boxShadow: `0 8px 24px ${alpha(secondaryMain, isDark ? 0.2 : 0.16)}`,
                  },
                }}
              >
                <CardActionArea
                  component={Link}
                  to={item.to}
                  sx={{
                    color: 'inherit',
                    height: '100%',
                    py: 1,
                    '&:hover': {
                      backgroundColor: alpha(secondaryMain, isDark ? 0.1 : 0.04),
                    },
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                    <Box display="flex" alignItems="flex-start" gap={1.5}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          bgcolor: alpha(primaryMain, isDark ? 0.22 : 0.1),
                          color: isDark ? colors.grey[100] : primaryMain,
                          border: `1px solid ${alpha(primaryMain, isDark ? 0.3 : 0.16)}`,
                        }}
                      >
                        <Icon sx={{ fontSize: 26 }} />
                      </Box>

                      <Box flex={1} minWidth={0}>
                        <Typography
                          variant="h6"
                          fontWeight={700}
                          sx={{
                            color: titleColor,
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.3,
                            mb: 0.75,
                          }}
                        >
                          {item.label}
                        </Typography>
                        <Typography variant="body2" sx={{ color: bodyColor, lineHeight: 1.5 }}>
                          {item.description}
                        </Typography>
                      </Box>

                      <ArrowForwardIcon
                        sx={{
                          fontSize: 20,
                          mt: 0.5,
                          flexShrink: 0,
                          color: alpha(secondaryMain, 0.85),
                        }}
                      />
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default ManageSystemDetailsPage;
